// Implements: F-UIX-021, F-UIX-007, F-UIX-009, F-VER-011, F-VER-014
/**
 * The version tree. Pure presentation.
 *
 * Nested lists of real links, not a custom `role="tree"`: a tree role promises
 * arrow-key navigation that this component would then have to implement, while
 * links are reached with Tab in reading order and opened with Enter for free
 * (F-UIX-007). The structure is still announced, because a nested list is.
 *
 * The current version is pointed out with TEXT (a `Badge` that says it) and
 * with `aria-current`, never with colour alone.
 */

import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import type { VersionTreeNode } from "../../../types/api";
import { dayText } from "../../../shared/ui/dayText";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { buildVersionForest } from "./versionForest";
import type { VersionBranch } from "./versionForest";
import styles from "./VersionTree.module.css";

export interface VersionTreeProps {
  readonly mockupId: string;
  readonly nodes: readonly VersionTreeNode[];
  readonly currentVersionId: string | null;
}

interface BranchListProps {
  readonly mockupId: string;
  readonly branches: readonly VersionBranch[];
  readonly currentVersionId: string | null;
}

function BranchList({ mockupId, branches, currentVersionId }: BranchListProps): ReactElement {
  const { t } = useTranslation();

  return (
    <ul className={styles.list}>
      {branches.map(({ node, children }) => {
        const current = node.id === currentVersionId;
        return (
          <li key={node.id} className={styles.item} data-current={current}>
            <span className={styles.row}>
              <Link
                className={styles.link}
                to={`/mockups/${encodeURIComponent(mockupId)}/versions/${encodeURIComponent(node.id)}`}
                aria-current={current ? "true" : undefined}
              >
                {t("versions.node", { n: node.sequence_number })}
              </Link>
              {current ? <Badge tone="info">{t("versions.current")}</Badge> : null}
              <span className={styles.created}>
                <span>{t("versions.created")}</span>
                <time dateTime={node.created_at}>{dayText(node.created_at)}</time>
              </span>
            </span>
            {children.length > 0 ? (
              <BranchList
                mockupId={mockupId}
                branches={children}
                currentVersionId={currentVersionId}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function VersionTree({ mockupId, nodes, currentVersionId }: VersionTreeProps): ReactElement {
  return (
    <BranchList
      mockupId={mockupId}
      branches={buildVersionForest(nodes)}
      currentVersionId={currentVersionId}
    />
  );
}
